import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import {
  getAiAccountStatisticsPage,
  getAiErrorRankStatistics,
  getAiTotalCountStatistics,
  getAiWeekTrendStatistics,
} from '@/_serveice/aiStatistics';
import StatCards from './components/StatCards';
import UsageTrendChart from './components/UsageTrendChart';
import ModelErrorChart from './components/ModelErrorChart';
import AccountTable from './components/AccountTable';
import styles from './styles.less';

/** 拉取当前筛选类型的全部账号，由表格做前端分页 */
const ACCOUNT_PAGE_SIZE = 999;

const getDefaultTime = () => [
  moment().subtract(30, 'day').startOf('day'),
  moment().endOf('day'),
];

const fmtTime = (date) => date.format('YYYY-MM-DD HH:mm:ss');

const AiStatistics = () => {
  const [timeRange, setTimeRange] = useState(getDefaultTime);
  const [type, setType] = useState('all');
  const [keyword, setKeyword] = useState('');
  const [searchKey, setSearchKey] = useState(0);
  const [totalStat, setTotalStat] = useState({});
  const [weekTrend, setWeekTrend] = useState([]);
  const [errorRank, setErrorRank] = useState([]);
  const [rows, setRows] = useState([]);
  const [videoRows, setVideoRows] = useState([]);
  const [loading, setLoading] = useState(false);

  /** 按筛选类型查询账号明细；type=all 时 type 传空字符串，由后端返回汇总 */
  const queryAccountList = useCallback(
    async ({ type, beginTime, endTime, empName }) => {
      setLoading(true);
      try {
        const res = await getAiAccountStatisticsPage({
          beginTime,
          endTime,
          empName: empName || undefined,
          type: type === 'all' ? '' : type,
          page: { pageNo: 1, pageSize: ACCOUNT_PAGE_SIZE },
        });
        setRows(res?.data?.datas || []);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  /** "视频生成成功率"卡片单独取一次视频类型 */
  const queryVideo = useCallback(async (beginTime, endTime) => {
    const res = await getAiAccountStatisticsPage({
      beginTime,
      endTime,
      type: 'video',
      page: { pageNo: 1, pageSize: ACCOUNT_PAGE_SIZE },
    });
    setVideoRows(res?.data?.datas || []);
  }, []);

  /** 总数统计 / 周趋势 / 错误率排行三个接口无入参，取一次即可 */
  useEffect(() => {
    getAiTotalCountStatistics().then((res) => setTotalStat(res?.data || {}));
    getAiWeekTrendStatistics().then((res) => setWeekTrend(res?.data || []));
    getAiErrorRankStatistics().then((res) => setErrorRank(res?.data || []));
    const [begin, end] = getDefaultTime();
    queryVideo(fmtTime(begin), fmtTime(end));
    queryAccountList({
      type: 'all',
      beginTime: fmtTime(begin),
      endTime: fmtTime(end),
      empName: '',
    });
  }, [queryAccountList, queryVideo]);

  const requestCurrent = (nextType) => {
    queryAccountList({
      type: nextType,
      beginTime: fmtTime(timeRange[0]),
      endTime: fmtTime(timeRange[1]),
      empName: keyword,
    });
    setSearchKey((key) => key + 1);
  };

  const handleSearch = () => requestCurrent(type);

  const handleTypeChange = (nextType) => {
    setType(nextType);
    requestCurrent(nextType);
  };

  return (
    <div className={styles.aiStatistics}>
      <h2 className={styles.pageTitle}>AI创作运营 · 统计看板</h2>
      <StatCards
        total={totalStat.total}
        success={totalStat.success}
        error={totalStat.error}
        videoRate={totalStat.successRate}
      />
      <div className={styles.chartRow}>
        <UsageTrendChart list={weekTrend} />
        <ModelErrorChart list={errorRank} />
      </div>
      <AccountTable
        key={searchKey}
        type={type}
        onTypeChange={handleTypeChange}
        rows={rows}
        loading={loading}
        keyword={keyword}
        onKeywordChange={setKeyword}
        timeRange={timeRange}
        onTimeChange={setTimeRange}
        onSearch={handleSearch}
      />
    </div>
  );
};

export default AiStatistics;
